import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

export default function AccessibilityStatement() {
  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Accessibility Statement — ElementPad" description="ElementPad accessibility commitments, WCAG 2.1 AA conformance, built-in accessibility tools, and known limitations." path="/accessibility" />
      <AppHeader />
      <Main className="py-10">
        <article className="max-w-[700px] mx-auto px-4 space-y-6 text-foreground">
          <header>
            <h1 className="text-3xl font-bold mb-1">Accessibility Statement</h1>
            <p className="text-muted-foreground text-sm">Last updated: May 26, 2026</p>
          </header>

          <section>
            <h2 className="text-lg font-semibold mb-1">Commitment</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ElementPad is committed to digital accessibility for all users. We strive to conform to <strong className="text-foreground">WCAG 2.1 Level AA</strong>, the <strong className="text-foreground">Americans with Disabilities Act (ADA)</strong>, and the <strong className="text-foreground">European Accessibility Act</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-1">What's working today</h2>
            <p className="text-sm text-muted-foreground mb-2">
              Verified in the May 26, 2026 code-level audit:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              <li>Single <code className="text-foreground">&lt;main&gt;</code> landmark and a "Skip to main content" link on every page</li>
              <li>Named landmarks for header, footer and primary navigation</li>
              <li>All icon-only buttons carry an <code className="text-foreground">aria-label</code></li>
              <li>Every <code className="text-foreground">&lt;img&gt;</code> has an <code className="text-foreground">alt</code> attribute; decorative images use <code className="text-foreground">alt=""</code></li>
              <li>No positive <code className="text-foreground">tabIndex</code> values; grids use roving tabindex with arrow-key navigation</li>
              <li>Polite + assertive ARIA live regions for game feedback</li>
              <li>Animations respect both <code className="text-foreground">prefers-reduced-motion</code> and the in-app pause toggle</li>
              <li>Semantic color tokens only — no hard-coded colors in components</li>
              <li>Page Structure panel lets keyboard and screen-reader users jump between headings and landmarks</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-1">Built-in Accessibility Tools</h2>
            <p className="text-sm text-muted-foreground mb-2">
              Use the floating accessibility button (bottom-right) to access:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              <li>Text scaling, adjustable line height &amp; letter spacing</li>
              <li>Readable and dyslexia-friendly fonts (OpenDyslexic)</li>
              <li>Contrast modes: high, dark, light, inverted</li>
              <li>Color-blindness simulations (protanopia, deuteranopia, tritanopia, grayscale)</li>
              <li>Saturation &amp; brightness controls</li>
              <li>Reading guide line and reading mask (spotlight)</li>
              <li>Highlight links &amp; headings, strong focus outlines</li>
              <li>Bigger cursor, hide images, pause animations, tooltips on hover</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-2">All settings persist across sessions via local storage.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-1">Technology</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Built with React 18, TypeScript, Tailwind CSS, and Radix UI accessible primitives. Compatible with NVDA, JAWS, VoiceOver, and TalkBack screen readers.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-1">Known Limitations</h2>
            <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
              These areas are tracked in the audit log and being improved:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              <li>3D Bohr model has no text-equivalent or keyboard alternative for the orbit animation</li>
              <li>Molecule, orbital and atom SVG viewers lack <code className="text-foreground">&lt;title&gt;</code>/<code className="text-foreground">&lt;desc&gt;</code> descriptions for screen readers</li>
              <li>Periodic table tiles convey element category through color; the category name is not yet in the accessible name</li>
              <li>Emission spectrum has no textual wavelength legend in the accessible tree</li>
              <li>Online Lab drag-and-drop interactions have no keyboard alternative yet</li>
              <li>No automated axe / Lighthouse run in CI; live screen-reader walkthroughs are done manually and infrequently</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-1">Disclaimer</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              While we strive for WCAG 2.1 AA compliance, we cannot guarantee full accessibility under all circumstances. This site is provided "as is." We are committed to addressing barriers in good faith.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-1">Contact</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Encounter a barrier? Open an issue or get in touch through the social media channels.
            </p>
          </section>
        </article>
      </Main>
    </div>
  );
}
